<template>
  <HandleSection
    class="positions-summary"
    :error="error"
    :loading="loading"
    section-title="Meus ativos"
    error-title="Não foi possível carregar os seus ativos"
    :refresh="fetchPositionsSummary"
  >
    <Heading class="positions-summary__title" :level="3">Meus ativos</Heading>

    <template v-if="hasPositions">
      <div
        v-for="position in positionsSummary"
        :key="position.description.value"
      >
        <HeadingDisplay
          v-if="position.error"
          class="positions-summary__item--error"
          :title="position.error.title"
          :subtitle="position.error.subtitle"
          size="extra-small"
        />
        <RouterLink
          v-else
          :to="{
            name: 'tradePositions',
            query: {
              group: position.description.value
            }
          }"
          :aria-label="`Ver detalhes ${position.description.formatted}`"
        >
          <Row class="positions-summary__row" has-interaction chevron>
            <template #top-left>
              <p
                :title="position.description.formatted"
                class="positions-summary__row__description"
              >
                {{ position.description.formatted }}
              </p>
            </template>
            <template #bottom-left>
              <p
                class="positions-summary__row__quantity"
                :title="position.content.quantity.formatted"
              >
                {{ position.content.quantity.formatted }}
              </p>
            </template>
            <template #top-right>
              <Money width="100%">
                <p class="positions-summary__row__balance">
                  {{ position.content.balance.formatted }}
                </p>
              </Money>
            </template>
          </Row>
        </RouterLink>
      </div>
      <RouterLink
        v-if="shouldRenderShowDetailsButton"
        :to="{
          name: 'tradePositions'
        }"
        aria-label="Ver detalhes das posições"
      >
        <Row has-interaction chevron>
          <template #top-left>
            <p class="positions-summary__text--bold">Ver detalhes</p>
          </template>
        </Row>
      </RouterLink>
    </template>
    <template v-else>
      <slot name="empty-state">
        <BaseText
          class="positions-summary__empty-text"
          :color="NebraskaColors.textSecondary"
          size="lg"
        >
          Você ainda não possui ativos.
        </BaseText>
      </slot>
    </template>
    <Row
      class="positions-summary__row"
      left-title="Ver minhas ordens"
      has-interaction
      chevron
      @click="goToOrders"
    >
      <template #icon-left>
        <InitialAvatarIcon icon="EA0090" size="extra-small" />
      </template>
    </Row>
  </HandleSection>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router/composables';

import {
  BaseText,
  Heading,
  Row,
  HeadingDisplay,
  InitialAvatarIcon
} from '@warrenbrasil/nebraska-web';
import { HandleSection } from '@/modules/trade/components/HandleSection';
import { Money } from '@/modules/common/components/MoneyV2';

import {
  getPositionsSummary,
  PositionsSummaryResponse
} from '@/modules/trade/services/instruments';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

const router = useRouter();

const error = ref(false);
const loading = ref(true);
const positionsSummary = ref<PositionsSummaryResponse | null>(null);

const hasPositions = computed(
  () => positionsSummary.value && positionsSummary.value.length > 0
);

const shouldRenderShowDetailsButton = computed(
  () => positionsSummary.value && positionsSummary.value.length > 1
);

const checkIfAllPositionsSummaryFailed = (
  positionsSummary: PositionsSummaryResponse
) => {
  return (
    positionsSummary.length > 0 &&
    positionsSummary.every(position => position.error)
  );
};

const fetchPositionsSummary = async () => {
  try {
    loading.value = true;
    error.value = false;

    const data = await getPositionsSummary();
    positionsSummary.value = data;

    if (checkIfAllPositionsSummaryFailed(positionsSummary.value))
      error.value = true;
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
};

function goToOrders() {
  router.push({ name: 'orders' });
}

onMounted(fetchPositionsSummary);
</script>

<style lang="less" scoped>
.positions-summary {
  padding-bottom: @size-spacing-x800;

  &__row {
    &__balance {
      white-space: nowrap;
    }

    &__description {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      word-break: break-all;
      white-space: normal;
      margin-right: @size-spacing-x150;
    }

    &__quantity {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      word-break: break-all;
      white-space: normal;
    }

    /deep/ .container-left {
      flex: auto;
    }
  }

  &__title {
    padding-bottom: @size-spacing-x300;
  }

  &__item {
    &--error {
      padding: @size-spacing-x300 0 @size-spacing-x350 0;
      border-top: @size-border-x400 solid @divider-primary;
    }
  }

  &__empty-text {
    border-top: @size-border-x400 solid @divider-primary;
    padding-top: @size-spacing-x350;
  }

  &__text {
    &--bold {
      font-weight: bold;
    }
  }
}
</style>
